import { Host, h } from "@stencil/core";
const MIN_VALUE = 0;
const TIME_TO_REMOVE_ANIMATION = 325; // 325ms
const INCREMENT_ADJUSTMENT_BETWEEN_STEPS = 1;
/**
 * @part ch-next-progress-bar__main-container - The main container of the control.
 * @part ch-next-progress-bar__caption - The caption displayed in the top of the control.
 * @part ch-next-progress-bar__steps-container - The container of the steps displayed below the caption of the control.
 * @part ch-next-progress-bar__step - Each step displayed in the control.
 * @part ch-next-progress-bar__description - The description displayed below the steps of the control.
 */
export class NextProgressBar {
    constructor() {
        this.shouldAddFadeInOutAnimation = false;
        this.caption = null;
        this.currentStep = MIN_VALUE;
        this.cssClass = undefined;
        this.description = null;
        this.presented = false;
        this.steps = 1;
    }
    /**
     * `true` if the componentDidLoad lifecycle method was executed
     */
    didLoad = false;
    incrementFromLastStep = 0;
    lastDescription;
    handleCurrentStepChange(newCurrentStep, oldCurrentStep) {
        this.incrementFromLastStep =
            newCurrentStep - oldCurrentStep - INCREMENT_ADJUSTMENT_BETWEEN_STEPS;
    }
    handleCaptionChange(_newDescription, oldDescription) {
        if (this.didLoad && !this.shouldAddFadeInOutAnimation) {
            this.shouldAddFadeInOutAnimation = true;
            this.lastDescription = oldDescription; // Store the last description
            setTimeout(() => {
                this.shouldAddFadeInOutAnimation = false;
            }, TIME_TO_REMOVE_ANIMATION);
        }
    }
    handlePresentedChange(newPresentedValue) {
        if (newPresentedValue) {
            this.incrementFromLastStep = this.currentStep;
        }
    }
    getStepPartName(stepValue, amountOfSteps) {
        let partName = "ch-next-progress-bar__step";
        if (stepValue <= amountOfSteps) {
            partName += " checked";
        }
        if (stepValue == amountOfSteps) {
            partName += " last";
        }
        return partName;
    }
    componentWillLoad() {
        if (this.presented) {
            this.incrementFromLastStep = this.currentStep;
        }
    }
    componentDidLoad() {
        this.didLoad = true;
    }
    render() {
        const calculatedCurrentStep = Math.max(this.currentStep, MIN_VALUE);
        const amountOfSteps = Math.max(this.steps, MIN_VALUE);
        const accessibilityAttributes = {
            role: "progressbar",
            "aria-labelledby": this.caption ? "title" : undefined,
            "aria-describedby": this.description ? "description" : undefined,
            "aria-valuemin": "0",
            "aria-valuemax": amountOfSteps.toString(),
            "aria-valuenow": calculatedCurrentStep.toString()
        };
        return (h(Host, { class: this.cssClass || null, "aria-hidden": !this.presented ? "true" : undefined }, this.presented && (h("div", { ...accessibilityAttributes, class: "progress-bar", part: "ch-next-progress-bar__main-container" }, !!this.caption && (h("h1", { id: "title", class: "title", part: "ch-next-progress-bar__caption" }, this.caption)), h("div", { class: "steps-container", style: {
                "--amount-of-steps": `${amountOfSteps}`
            }, part: "ch-next-progress-bar__steps-container" }, [...Array(amountOfSteps).keys()].map((step) => (h("div", { class: {
                step: true,
                "step--checked": step <= calculatedCurrentStep,
                "step--last-checked": step == calculatedCurrentStep
            }, part: this.getStepPartName(step, amountOfSteps), style: {
                "--delay": calculatedCurrentStep - step <= this.incrementFromLastStep
                    ? (this.incrementFromLastStep -
                        calculatedCurrentStep +
                        step).toString()
                    : "0"
            } })))), !!this.description && (h("p", { id: "description", class: {
                description: true,
                "description-transition": this.shouldAddFadeInOutAnimation
            }, part: "ch-next-progress-bar__description" }, this.shouldAddFadeInOutAnimation
            ? this.lastDescription
            : this.description))))));
    }
    static get is() { return "ch-next-progress-bar"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["next-progress-bar.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["next-progress-bar.css"]
        };
    }
    static get properties() {
        return {
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "It specifies the main text that is shown on the progress."
                },
                "attribute": "caption",
                "reflect": false,
                "defaultValue": "null"
            },
            "currentStep": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the value of the progress."
                },
                "attribute": "current-step",
                "reflect": false,
                "defaultValue": "MIN_VALUE"
            },
            "cssClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class to set as the `ch-next-progress-bar` element class."
                },
                "attribute": "css-class",
                "reflect": false
            },
            "description": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "It specifies more information that is shown on the progress."
                },
                "attribute": "description",
                "reflect": false,
                "defaultValue": "null"
            },
            "presented": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the progress bar is rendered."
                },
                "attribute": "presented",
                "reflect": false,
                "defaultValue": "false"
            },
            "steps": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify the amount of steps for the progress."
                },
                "attribute": "steps",
                "reflect": false,
                "defaultValue": "1"
            }
        };
    }
    static get states() {
        return {
            "shouldAddFadeInOutAnimation": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "currentStep",
                "methodName": "handleCurrentStepChange"
            }, {
                "propName": "description",
                "methodName": "handleCaptionChange"
            }, {
                "propName": "presented",
                "methodName": "handlePresentedChange"
            }];
    }
}
//# sourceMappingURL=next-progress-bar.js.map
